import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { erApiFeil, erNettfeil, type ApiFeil } from '../api/feil.ts';
import { useFriskOppHypotese } from '../api/useHypoteseBehandling.ts';
import { useHypoteseConfig, useHypoteseMedNummer, type Hypothesis, type HypothesisSummary } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { Kartknapp } from '../innboks/Kartknapp.tsx';
import type { Tast } from '../innboks/Tastaturkart.tsx';
import { Toast, type ToastInnhold } from '../innboks/Toast.tsx';
import { useTilgang } from '../skall/tilgang.ts';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Fot } from './deler.tsx';
import { glemArk, HDetalj } from './HDetalj.tsx';
import { Gruppetittel, HypRad, Skjelettrader } from './HListe.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { fornavn, navnAv, oppramsing, statusNavn, stor } from './lesning.ts';
import { erHBehandler, erHLeserPluss } from './rommet.ts';
import { useAngre, type Ventende } from './useAngre.ts';

/**
 * Lista og detaljen i rommet Hypoteser — innboksen og «Til deg» (DD-57: sidemeny 216 · lista
 * 384 · detaljen resten). Adressen bærer nummeret (/h/innboks/142), så en lenke til en
 * hypotese er en lenke, og j/k flytter i lista også fra detaljen. Under 900 px er lista og
 * detaljen to steg (DD-51): uten nummer står lista, med nummer står detaljen med tilbakepil.
 *
 * Siden eier angre-vinduet (`useAngre`) og toasten, så en overgang som venter, overlever at
 * detaljen bytter rad — og sendes straks når siden forlates.
 */

export const H_KART: Tast[] = [
  ['j / k', 'neste og forrige i lista — også fra detaljen'],
  ['/', 'søk på nummer (142 eller H-142)'],
  ['n', 'internt notat (leser og opp)'],
  ['s t', 'Trenger mer …'],
  ['s p', 'Parker til … (vurderes) · Sett på pause … (valgt, testes)'],
  ['s v', 'Velg'],
  ['s a', 'Avvis'],
  ['s o', 'Ta tilbake til vurdering'],
  ['s f', 'Fortsett etter pause'],
  ['s s', 'Stopp testen …'],
  ['s b', 'Bekreft konklusjonen'],
  ['Ctrl+Enter', 'fotens knapp'],
  ['Ctrl+S', 'lagre notatet'],
  ['Esc', 'lukker arket eller kartet — aldri siden'],
];

export interface Gruppe {
  navn: string | null;
  rader: HypothesisSummary[];
}

export function Arbeidsflate({
  tittel,
  base,
  nr,
  grupper,
  laster,
  feil,
  topp,
  tittelRad,
  onSok,
  tom,
  kartTittel,
  alene = false,
  etter,
  kort,
  melding,
  venter,
  detalj: annenDetalj,
  faktor,
}: {
  tittel: string;
  base: string;
  nr: number | null;
  grupper: Gruppe[];
  laster: boolean;
  /** Lista kunne ikke hentes: meldingen med «Prøv igjen», aldri «ingen» (ux-flow-architect). */
  feil: ReactNode | null;
  topp?: ReactNode;
  /**
   * Det som deler rad med tittelen (R4.4-grepet under 900 px: søk-ikonet og rekkefølgen), etter
   * kartknappen. Over grensen sender sidene ingenting — tittelen står alene på raden.
   */
  tittelRad?: ReactNode;
  /** `/` når søkefeltet ikke er i DOM (under 900 px er det et ikon til det brukes): siden åpner det. */
  onSok?: () => void;
  /** Hele flaten når lista er tom uten filter (DD-19): en overskrift, en setning med sanne tall. */
  tom: ReactNode | null;
  kartTittel: string;
  /**
   * Registeret (W9, konseptets skjerm 6): lista over hele flaten og detaljen alene, aldri side om
   * side — i begge bredder, som under 900 px ellers. Ingen rad velges av seg selv, og raden er
   * registerets (tallet til høyre er datoen eller fristen som gjelder, meta-linjen hvem som har den).
   */
  alene?: boolean;
  /** Under lista — «Vis flere» når tjeneren har flere sider. */
  etter?: ReactNode;
  /**
   * Det som krever deg, over gruppene (M5c: eierforslaget i «Til deg», som spørsmålskortet øverst
   * i W7). Står i listekolonnen, i begge bredder.
   */
  kort?: ReactNode;
  /** En kvittering fra siden selv (en ny `n` viser den på nytt) — samme toast som overgangene. */
  melding?: { tekst: string; feil?: boolean; n: number } | null;
  /** H3.1: notisens mottakere per hypotese-id, for «Til deg»-radens tall («37 venter»). */
  venter?: Readonly<Record<string, number>>;
  /**
   * H3.2: noe annet enn en hypotese i detaljen — møtet beslutningstakeren skal bekrefte, i «Til deg»
   * (W9). Lista står som før; ingen rad er valgt, og første rad velges ikke av seg selv.
   */
  detalj?: ReactNode;
  /** H3.2: registeret er sortert på én faktor — raden viser den (svar 4). */
  faktor?: 'impact' | 'confidence' | 'effort';
}) {
  const smal = useSmal();
  const naviger = useNavigate();
  // Adressen bærer rekkefølgen (?sort=nyest, DD-74): et radvalg skal ikke nullstille den.
  const { search, state, pathname } = useLocation();
  const tilgang = useTilgang();
  const me = useMe();
  const config = useHypoteseConfig();
  const megId = me.data?.id;
  const rom = useMemo(() => ({ behandler: erHBehandler(tilgang), leserPluss: erHLeserPluss(tilgang) }), [tilgang]);
  const rader = useMemo(() => grupper.flatMap((g) => g.rader), [grupper]);
  const detalj = useHypoteseMedNummer(nr);
  const friskOpp = useFriskOppHypotese();

  // Juster-siden sender kvitteringen med når den går tilbake til detaljen (konseptets skjerm 6b).
  const [toast, setToast] = useState<ToastInnhold | null>(() => {
    const kvittering = (state as { kvittering?: unknown } | null)?.kvittering;
    return typeof kvittering === 'string' ? { tekst: kvittering } : null;
  });
  // Kvitteringen er vist: ut av historikken, så en ny innlasting ikke viser den igjen.
  useEffect(() => {
    if ((state as { kvittering?: unknown } | null)?.kvittering !== undefined) void naviger(`${pathname}${search}`, { replace: true, state: null });
  }, [state, naviger, pathname, search]);
  const [feilPaa, setFeilPaa] = useState<{ id: string; e: ApiFeil } | null>(null);
  useEffect(() => {
    if (melding) setToast({ tekst: melding.tekst, feil: melding.feil });
  }, [melding]);

  const tilRad = useCallback((h: HypothesisSummary | undefined) => (h?.number !== undefined ? void naviger(`${base}/${h.number}${search}`) : undefined), [base, naviger, search]);

  const behandleFeil = useCallback(
    (id: string, nummer: number | undefined, e: unknown) => {
      if (!erApiFeil(e)) {
        setToast({ tekst: 'Noe gikk galt. Det du skrev står. Prøv igjen.', feil: true });
        return;
      }
      if (erNettfeil(e)) {
        setToast({ tekst: 'Ikke sendt — ingen nett. Det du skrev står. Prøv igjen når nettet er tilbake.', feil: true });
        return;
      }
      setFeilPaa({ id, e });
      // Tjeneren kom først, eller lista har endret seg: detaljen viser det som faktisk skjedde.
      if (['invalid_status_transition', 'needs_more_limit', 'not_decider', 'own_hypothesis', 'decider_is_submitter', 'not_owner', 'not_reviewer', 'conclusion_missing', 'conclusion_frozen', 'wording_locked', 'conclusion_notice_already_sent', 'conclusion_notice_no_recipients', 'evidence_locked', 'external_link_not_found'].includes(e.code)) {
        friskOpp(id, nummer);
        // H3.1: notisen er avgjort et annet sted, eller grunnlaget er låst — si det, så teksten ikke står uforklart.
        if (e.code.startsWith('conclusion_notice') || e.code === 'evidence_locked' || e.code === 'external_link_not_found') setToast({ tekst: e.brukertekst, feil: true });
        return;
      }
      if (!['revisit_date_out_of_range', 'template_unmodified', 'owner_is_decider', 'deadline_cannot_be_extended', 'reason_code_required', 'open_blocking_required', 'data_limitations_required', 'threshold_not_numeric'].includes(e.code)) {
        setToast({ tekst: e.brukertekst, feil: true });
      }
    },
    [friskOpp],
  );

  const angre = useAngre({
    onSendt: (v: Ventende, h: Hypothesis) => {
      glemArk(v.id);
      setFeilPaa((f) => (f?.id === v.id ? null : f));
      const hvem = fornavn(h.submittedBy);
      const nr = nummerTekst(h.number);
      if (v.overgang.handling === 'merge') {
        // H3.3 (W13): svaret er MÅLET — detaljen går dit, med begge innsenderne.
        const fraDen = (h.coSubmitters ?? []).filter((c) => c.fromHypothesis.number === v.nummer).map((c) => navnAv(c.admin));
        setToast({ tekst: `${nummerTekst(v.nummer)} er slått sammen med ${nr}. Svaret er sendt til ${oppramsing([...new Set([...fraDen, navnAv(h.submittedBy)])])}.` });
        if (h.number !== undefined) void naviger(`${base}/${h.number}${search}`);
        return;
      }
      const tekst: Partial<Record<Ventende['overgang']['handling'], string>> = {
        needsMore: `Spørsmålet er sendt til ${hvem}.`,
        approveAdjustment: `Justeringen er godkjent. ${stor(hvem)} har fått svaret.`,
        declineAdjustment: `Justeringen er avslått. ${stor(hvem)} har fått svaret.`,
        confirmConclusion: `${nr} er konkludert: ${statusNavn(config.data, h).toLowerCase()}. Svaret er sendt til ${navnAv(h.submittedBy)}.`,
        resume: `${nr} → ${statusNavn(config.data, h)}. ${stor(hvem)} får beskjed.`,
        // H3.1: notisen går til sluttbrukerne gjennom leietakerens egen kanal, aldri som push herfra.
        sendNotice: `Notisen er sendt til ${h.conclusionNotice?.recipientCount ?? h.evidenceReach}. ${me.data?.tenantName ?? 'Leietakeren'} varsler i egen kanal.`,
        skipNotice: `Ingen notis for ${nr}. Den er ute av «Til deg».`,
        removeEvidence: `Saken er tatt ut av grunnlaget for ${nr}.`,
        // H3.5: lenken er fjernet hos tjeneren nå; ventetoasten sa navnet.
        removeLink: `Lenken er fjernet fra ${nr}.`,
      };
      setToast({ tekst: tekst[v.overgang.handling] ?? `${nr} → ${statusNavn(config.data, h)}. ${stor(hvem)} har fått svaret.` });
    },
    onFeil: (v, e) => behandleFeil(v.id, v.nummer, e),
  });

  // Over 900 px velges første rad når lista kommer (DD-57). Under er lista det man ser først.
  const foerste = rader[0];
  const autovelg = !smal && !alene && nr === null && annenDetalj === undefined && foerste?.number !== undefined;

  // j/k flytter i lista, også fra detaljen; / går til søket (DD-11). Aldri mens et felt har fokus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const maal = e.target as HTMLElement | null;
      if (maal && /^(INPUT|TEXTAREA|SELECT)$/.test(maal.tagName)) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      if (e.key === 'j' || e.key === 'k') {
        if (rader.length === 0) return;
        const i = rader.findIndex((x) => x.number === nr);
        const til = rader[e.key === 'j' ? Math.min(rader.length - 1, i + 1) : Math.max(0, i === -1 ? 0 : i - 1)];
        tilRad(til);
      } else if (e.key === '/') {
        const sok = document.getElementById('hb-sok');
        if (sok) {
          e.preventDefault();
          sok.focus();
        } else if (onSok) {
          e.preventDefault();
          onSok();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rader, nr, tilRad, onSok]);

  if (autovelg) return <Navigate to={`${base}/${foerste!.number}${search}`} replace />;

  const indeks = rader.findIndex((x) => x.number === nr);
  const posisjon = indeks >= 0 ? `${indeks + 1} av ${rader.length}` : undefined;
  const neste = indeks >= 0 ? (rader[indeks + 1] ?? rader[indeks - 1]) : rader[0];
  const h = detalj.data ?? null;

  const toastInnhold: ToastInnhold | null = angre.vent
    ? { tekst: angre.vent.v.tekst, sekunder: angre.vent.sekunder, angre: () => void angre.angre(), ikon: 'checkmark-circle-outline' }
    : toast;

  const detaljFlate =
    annenDetalj !== undefined ? (
      annenDetalj
    ) : nr === null ? (
      <section className="hb-detalj hb-detalj-tom" aria-label="Ingen hypotese valgt" />
    ) : detalj.isPending ? (
      <section className="hb-detalj" aria-label="Henter hypotesen">
        <Skjelettrader etikett="Henter hypotesen" />
      </section>
    ) : detalj.isError || h === null ? (
      <section className="hb-detalj" aria-label="Fant ikke hypotesen">
        <div className="hb-detalj-innhold">
          {/* K2: en hypotese som ikke finnes, sier det samme som en du ikke har tilgang til. */}
          <h2 className="panel-h2">Fant ikke {nummerTekst(nr)}</h2>
          <p className="hb-dempet">Den finnes ikke, eller den er ikke din å se. Sjekk nummeret.</p>
        </div>
      </section>
    ) : (
      <HDetalj
        key={h.id}
        h={h}
        config={config.data}
        megId={megId}
        megNavn={me.data?.displayName}
        rom={rom}
        fra={{ navn: tittel, til: `${base}${search}` }}
        alene={alene}
        posisjon={posisjon}
        justerTil={`${base}/${h.number}/juster`}
        venter={angre.vent?.v.id === h.id ? angre.vent.v : null}
        onHold={(v) => {
          setFeilPaa(null);
          setToast(null);
          angre.start(v);
        }}
        feil={feilPaa?.id === h.id ? feilPaa.e : null}
        onNeste={neste && neste.number !== nr ? () => tilRad(neste) : undefined}
        onKvittering={(tekst) => {
          setFeilPaa(null);
          setToast({ tekst });
        }}
        onFeil={(e) => behandleFeil(h.id, h.number, e)}
      />
    );

  const liste = (
    <section className={alene ? 'hb-liste hb-liste-alene' : 'hb-liste'} aria-label={tittel}>
      <div className="hb-liste-rull">
        <div className="hb-liste-topp">
          <div className="hb-liste-tittel">
            {/* tabIndex −1: fokus kan settes hit når det den sto på, forsvinner (eierforslaget er avslått). */}
            <h1 className="panel-h1" id="hb-liste-h" tabIndex={-1}>
              {tittel}
            </h1>
            <Kartknapp tittel={kartTittel} taster={H_KART} />
            {tittelRad}
          </div>
          {topp}
        </div>
        {laster ? (
          <Skjelettrader etikett="Laster" />
        ) : feil ? (
          <div className="hb-liste-melding">{feil}</div>
        ) : (
          <>
            {kort}
            <ul className="hb-rader">
              {grupper.map((g) => (
                <GruppeRader key={g.navn ?? 'alle'} g={g} config={config.data} megId={megId} valgtNr={smal ? null : nr} smal={smal} register={alene} venter={venter} faktor={faktor} onVelg={tilRad} />
              ))}
            </ul>
          </>
        )}
        {!laster && !feil ? etter : null}
      </div>
      {smal ? (
        <Fot primar={{ label: 'Ny hypotese', onClick: () => void naviger('/h/ny'), ikon: 'add-outline', tast: 'ingen', testid: 'h-ny-fot' }} />
      ) : (
        <div className="hb-liste-fot" aria-hidden="true">
          <kbd>J</kbd>
          <kbd>K</kbd> neste og forrige · <kbd>N</kbd> notat · <kbd>?</kbd> alle
        </div>
      )}
    </section>
  );

  const innhold = tom && !laster && !feil ? (
    <div className="hb-tomflate">{tom}</div>
  ) : smal || alene ? (
    nr === null && annenDetalj === undefined ? (
      liste
    ) : alene && !smal ? (
      // Detaljen alene over hele flaten (registeret): beholderen gir den høyden lista ellers tar.
      <div className="hb-alene">{detaljFlate}</div>
    ) : (
      detaljFlate
    )
  ) : (
    <div className="hb-todelt">
      {liste}
      {detaljFlate}
    </div>
  );

  return (
    <>
      {innhold}
      {toastInnhold ? <Toast innhold={toastInnhold} onLukk={() => setToast(null)} /> : null}
    </>
  );
}

function GruppeRader({ g, config, megId, valgtNr, smal, register, venter, faktor, onVelg }: { g: Gruppe; config: Parameters<typeof HypRad>[0]['config']; megId: string | undefined; valgtNr: number | null; smal: boolean; register: boolean; venter?: Readonly<Record<string, number>>; faktor?: 'impact' | 'confidence' | 'effort'; onVelg: (h: HypothesisSummary) => void }) {
  return (
    <>
      {g.navn ? (
        <Gruppetittel>
          {g.navn} · {g.rader.length}
        </Gruppetittel>
      ) : null}
      {g.rader.map((h) => (
        <HypRad key={h.id} h={h} config={config} megId={megId} valgt={h.number === valgtNr} smal={smal} register={register} venter={venter?.[h.id]} faktor={faktor} onVelg={() => onVelg(h)} />
      ))}
    </>
  );
}
